/**
 * ModalShell — contenedor de modal estándar, una sola implementación para toda
 * la app. Resuelve H7 de docs/UX_VISION.md:
 *
 * - `body`-scroll-lock mientras está abierto (la página de atrás no scrollea)
 * - header FIJO / cuerpo scrolleable / footer FIJO opcional — el título y el
 *   botón de cerrar no se van al hacer scroll de contenido largo
 * - foco atrapado, `Esc` y click en el scrim cierran, devuelve el foco al
 *   elemento que abrió el modal
 * - scrim `position: fixed` con z-index alto — escapa de cualquier contenedor
 *   igual que el `.modal-overlay` anterior. NO se portalea a <body>: el
 *   contenido (`children`) usa tokens del contexto donde vive (Apple HIG en el
 *   portal, Contexto A en booking) y esos custom properties SÍ se heredan por
 *   el árbol de React — un portal a <body> los dejaría sin resolver.
 *
 * El shell en sí usa tokens `--ccui-*` contexto-neutrales (igual que
 * ConfirmDialog/Toast, esos sí portaleados porque son self-contained).
 *
 * El padre controla el montaje (`{open && <ModalShell …>}`) y el `onClose`; si
 * necesita bloquear el cierre (ej. mientras hay una acción en curso), pasa un
 * `onClose` que lo contemple.
 */
import React, { useEffect, useId, useRef } from 'react';
import './ui.css';

interface ModalShellProps {
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Bloque fijo al pie (acciones). Si se omite, no se renderiza el footer. */
  footer?: React.ReactNode;
  /** Ancho máximo del card: sm 360 · md 420 (default) · lg 560. */
  size?: 'sm' | 'md' | 'lg';
  /** Franja de color a la izquierda del card (ej. color del tipo de terapia). */
  accentColor?: string;
  /** Texto accesible del botón de cerrar. */
  closeLabel?: string;
}

const ModalShell: React.FC<ModalShellProps> = ({
  title,
  onClose,
  children,
  footer,
  size = 'md',
  accentColor,
  closeLabel = 'Cerrar',
}) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const lastFocused = useRef<HTMLElement | null>(null);
  const titleId = useId();

  // ref al onClose más reciente — el listener de teclado se registra una sola vez
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    lastFocused.current = document.activeElement as HTMLElement | null;
    // Scroll-lock. El scroller real depende del layout donde vive el modal:
    // en booking (Contexto A) scrollea `<html>`/`<body>`; en el portal
    // (Contexto B) el scroller es `.portal-main` (tiene `overflow-y:auto`), no
    // el documento — bloquear solo `body` dejaba la página de atrás scrolleando
    // detrás del scrim. Bloqueamos los 3; el que no aplica es un no-op.
    const locked: Array<[HTMLElement, string]> = [
      document.documentElement,
      document.body,
      document.querySelector<HTMLElement>('.portal-main'),
    ].filter((el): el is HTMLElement => el != null)
      .map(el => [el, el.style.overflow]);
    locked.forEach(([el]) => { el.style.overflow = 'hidden'; });
    closeBtnRef.current?.focus();
    return () => {
      locked.forEach(([el, prev]) => { el.style.overflow = prev; });
      lastFocused.current?.focus?.();
    };
  }, []);

  // Esc + trampa de foco a nivel `document` (capture): el listener en el div solo
  // dispara si el foco vive dentro, pero acá el contenido cambia (Reagendar
  // desmonta el botón que tenía foco → foco cae a <body>) y perderíamos Esc.
  // Si hay un ConfirmDialog encima (`.ccui-dialog-scrim`), este modal no
  // interfiere — el diálogo maneja su propio Esc.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (document.querySelector('.ccui-dialog-scrim')) return;
      const card = cardRef.current;
      if (!card) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== 'Tab') return;

      const focusables = card.querySelectorAll<HTMLElement>(
        'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (!card.contains(active)) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handler, true);
    return () => document.removeEventListener('keydown', handler, true);
  }, []);

  return (
    <div
      className="ccui-modal-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={cardRef}
        className={`ccui-modal ccui-modal--${size}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={accentColor ? { borderLeft: `4px solid ${accentColor}` } : undefined}
      >
        <header className="ccui-modal__header">
          <h2 id={titleId} className="ccui-modal__title">
            {title}
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            className="ccui-modal__close"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <i className="fa-solid fa-xmark" aria-hidden="true" />
          </button>
        </header>

        <div className="ccui-modal__body">{children}</div>

        {footer != null && <footer className="ccui-modal__footer">{footer}</footer>}
      </div>
    </div>
  );
};

export default ModalShell;
